// EN
export const accomMgmt = {
  common: {
    cancel: "Cancel",
    save: "Save",
    update: "Update",
    back: "Back",
    continue: "Continue",
    optional: "(optional)",
    optionalPlaceholder: "Optional",
    processing: "Processing...",
    name: "Name",
    description: "Description",
    nameRequired: "Name is required.",
  },

  type: {
    apartment: "Tourist apartment",
    hotel: "Hotel",
    camping: "Campsite",
    villa: "Villa",
  },

  step: {
    type: "Type",
    structure: "Structure",
    accommodation: "Accommodation",
  },

  step1: {
    title: "What type of accommodation do you have?",
    subtitle: "Select the accommodation type.",
    subtitleQuota: "Only the types with quota left on your plan will be active.",
    subtitleFirst: "Since this is your first accommodation, you can choose any type.",
    remaining: "remaining: {count}",
    loadingQuota: "Loading your plan's quota…",
  },

  step2: {
    title: "Set up your accommodation",
    subtitle:
      "Decide whether it will be a standalone accommodation or one with multiple calendars (several units under a single accommodation).",
    singleTitle: "A single space",
    singleChip: "1 calendar",
    singleDesc: "Rent the whole apartment using a single calendar.",
    groupedTitle: "Multiple spaces",
    groupedChip: "Multiple calendars",
    groupedDesc: "You rent several independent spaces within the apartment",
  },

  step3: {
    title: "Set up the main accommodation",
    subtitle:
      "This will be the “container” (Listing) that groups several calendars. E.g. “Castle Suite”.",
    panelTitle: "Choose an option",
    availableCount: "You have {count} grouped accommodations available.",
    noneAvailable: "No grouped listings available for this type (create a new one).",
    newTitle: "Create a new main accommodation",
    newDesc: "You define the name of the main accommodation.",
    nameLabel: "Main accommodation name",
    namePlaceholder: "E.g. Castle Suite",
    nameHelp: "This name will be saved and you'll be able to group several calendars.",
    minChars: "Enter at least 3 characters.",
    existingTitle: "Add to an existing accommodation",
    existingDesc: "The unit will sit within an accommodation you've already created.",
    selectLabel: "Select the accommodation",
    selectPlaceholder: "Select an accommodation…",
    listingOption: "{name} · {units} unit(s)",
    ready: "Ready",
    noneForType: "You have no grouped accommodations available for this type.",
    tip: "Tip: this “main” one will be the container the new unit is added to.",
  },

  preview: {
    tab: "youraccommodation",
    perNight: "/night",
    book: "Book",
    locationPlaceholder: "Country, city, street",
    guests: "{count} guest | {count} guests",
    bedrooms: "{count} bedroom | {count} bedrooms",
    bathrooms: "{count} bathroom | {count} bathrooms",
    amenitiesTitle: "Amenities",
    amenitiesEmpty: "Select to add amenities to your accommodation",
    locationTitle: "Location",
    locationEmpty: "Set the location",
    descriptionTitle: "Description",
    descriptionEmpty: "Select to add a description to your accommodation",
    namePlaceholder: "Accommodation name",
    amenity: {
      air_conditioning: "Air conditioning",
      heating: "Central heating",
      parking: "Parking space",
      oven: "Oven and hob",
      pets_allowed: "Pets allowed",
      wifi: "WiFi internet",
      essentials: "Essentials",
      washing_machine: "Washing machine",
      children_allowed: "Children allowed",
      refrigerator: "Fridge",
    },
  },

  amenities: {
    title: "Equipment and amenities",
    subtitle: "Tell guests what your accommodation offers so they know what to expect.",
    searchPlaceholder: "Search amenity…",
    selectedCount:
      "{count} amenity selected | {count} amenities selected",
    noResults: 'No amenities found for "{search}".',
    cat: {
      all: "All",
      basics: "Essentials",
      bathroom: "Bathroom",
      bedroom: "Bedroom and laundry",
      kitchen: "Kitchen and dining",
      outdoor: "Outdoor",
      leisure: "Leisure and entertainment",
      safety: "Safety",
      family: "Families",
      parking: "Parking",
      services: "Services",
      connectivity: "Connectivity",
      accessibility: "Accessibility",
    },
    item: {
      wifi: "WiFi",
      essentials: "Essentials",
      air_conditioning: "Air conditioning",
      heating: "Heating",
      indoor_fireplace: "Indoor fireplace",
      hot_water: "Hot water",
      hangers: "Hangers",
      bed_linen: "Bed linen",
      towels: "Towels",
      shampoo: "Shampoo",
      shower_gel: "Shower gel",
      conditioner: "Conditioner",
      hair_dryer: "Hair dryer",
      bathtub: "Bathtub",
      bidet: "Bidet",
      jacuzzi: "Jacuzzi",
      outdoor_shower: "Outdoor shower",
      washing_machine: "Washing machine",
      dryer: "Dryer",
      iron: "Iron",
      clothes_drying_rack: "Clothes drying rack",
      extra_pillows: "Extra pillows and blankets",
      wardrobe: "Clothing storage",
      blackout_curtains: "Blackout blinds or curtains",
      nearby_laundry: "Laundry nearby",
      oven: "Oven",
      refrigerator: "Fridge",
      freezer: "Freezer",
      microwave: "Microwave",
      dishwasher: "Dishwasher",
      cooking_basics: "Basic cooking utensils",
      plates_cutlery: "Dishes and cutlery",
      wine_glasses: "Wine glasses",
      coffee_maker: "Coffee maker",
      coffee: "Coffee",
      kettle: "Kettle",
      toaster: "Toaster",
      blender: "Blender",
      rice_cooker: "Rice cooker",
      bread_maker: "Bread maker",
      mini_fridge: "Mini fridge",
      stovetop: "Stovetop",
      dining_table: "Dining table",
      baking_sheet: "Baking sheet",
      bbq_grill: "Barbecue",
      bbq_utensils: "Barbecue utensils",
      outdoor_kitchen: "Outdoor kitchen",
      outdoor_dining: "Outdoor dining area",
      breakfast: "Breakfast",
      pool: "Pool",
      shared_pool: "Shared pool",
      garden: "Backyard",
      patio_balcony: "Patio or balcony",
      outdoor_furniture: "Outdoor furniture",
      sun_loungers: "Sun loungers",
      hammock: "Hammock",
      fire_pit: "Outdoor fire pit",
      shared_fireplace: "Shared fireplace",
      sauna: "Sauna",
      beach_essentials: "Beach essentials",
      beach_access: "Beach access",
      lake_access: "Lake access",
      resort_access: "Resort access",
      waterfront: "Waterfront",
      dock: "Boat dock",
      kayak: "Kayak",
      bikes: "Bikes",
      ski_slope: "Ski slope",
      outdoor_playground: "Outdoor playground",
      tv: "TV",
      sound_system: "Sound system",
      record_player: "Record player",
      piano: "Piano",
      gym: "Gym",
      exercise_equipment: "Exercise equipment",
      games: "Board games",
      giant_games: "Giant games",
      pool_table: "Pool table",
      ping_pong: "Ping-pong table",
      bowling: "Bowling alley",
      arcade: "Arcade games",
      minigolf: "Minigolf",
      batting_cage: "Batting cage",
      ice_rink: "Ice hockey rink",
      laser_tag: "Laser tag",
      skate_ramp: "Skate ramp",
      climbing_wall: "Climbing wall",
      game_console: "Game console",
      books: "Books and reading material",
      cinema: "Cinema",
      themed_room: "Themed room",
      smoke_detector: "Smoke detector",
      co_detector: "Carbon monoxide detector",
      fire_extinguisher: "Fire extinguisher",
      first_aid_kit: "First aid kit",
      fire_guard: "Fire guard",
      safe: "Safe",
      gated_community: "Gated community",
      security_cameras: "Security cameras",
      children_allowed: "Children allowed",
      crib: "Crib",
      travel_cot: "Travel cot",
      baby_bath: "Baby bath",
      baby_safety_gates: "Baby safety gates",
      changing_table: "Changing table",
      high_chair: "High chair",
      children_plates: "Children's dishes and cutlery",
      children_books_toys: "Children's books and toys",
      kids_bikes: "Kids' bikes",
      outlet_covers: "Outlet covers",
      table_corner_guards: "Table corner guards",
      window_guards: "Window guards",
      baby_monitor: "Baby monitor",
      children_game_room: "Children's game room",
      babysitter_recommendations: "Babysitter recommendations",
      parking: "Free parking on the premises",
      free_street_parking: "Free street parking",
      paid_parking_on_premises: "Paid parking on the premises",
      paid_parking_off_premises: "Paid parking off the premises",
      ev_charger: "Electric car charger",
      e_bike_charger: "E-bike charger",
      pets_allowed: "Pets allowed",
      long_term_stays: "Available for long stays",
      luggage_dropoff: "Luggage drop-off allowed",
      cleaning_during_stay: "Cleaning available during the stay",
      concierge: "Concierge",
      cleaning_products: "Cleaning products",
      recycling: "Recycling bins",
      compactor: "Waste compactor",
      compost: "Compost bin",
      solar_panels: "Solar panels",
      workspace: "Workspace",
      private_entrance: "Private entrance",
      private_lounge: "Private lounge",
      mosquito_net: "Mosquito net",
      ceiling_fan: "Ceiling fan",
      portable_fans: "Portable fans",
      ethernet: "Ethernet connection",
      mobile_wifi: "Mobile WiFi",
      single_floor: "Single-level accommodation",
      elevator: "Elevator",
    },
  },

  room: {
    createTitle: "Register room type",
    editTitle: "Update room type",
    namePlaceholder: "E.g. Double Room",
    countLabel: "Number of rooms of this type",
    countPlaceholder: "E.g. 20",
    countHint: "This is the total inventory for the type. E.g. “Double” = 20 rooms.",
    capacityLabel: "Total capacity (max. guests)",
    capacityPlaceholder: "E.g. 5",
    capacityHint:
      "This is the real guest limit. Even if you set per-category maximums, the total prevails.",
    maxAdults: "Max. Adults",
    maxChildren: "Max. Children",
    maxInfants: "Max. Infants",
    importantLabel: "Important:",
    capacityNote: "these are per-category caps. The total allowed is set by “Total capacity”.",
    physicalTitle: "Physical rooms (RoomInstances)",
    physicalDesc: "Enable it if you want to manage 101/102, per-room maintenance and locks.",
    generateLabel: "Generate/Adjust physical rooms automatically",
    generatorTitle: "Code generator",
    prefixLabel: "Prefix",
    prefixPlaceholder: "E.g. 10 or A-",
    prefixHint: "E.g. prefix “10” + start 1 => 101,102...",
    startLabel: "Start",
    paddingLabel: "Padding",
    paddingHint: "E.g. pad 3 => 001,002,003",
    nameModeType: "Type only",
    nameModeTypeCode: "Type + code",
    defaultDescLabel: "Default description",
    willCreatePre: "Up to",
    willCreatePost: "physical rooms will be created or adjusted automatically.",
    errors: {
      saveFallback: "Error saving room type.",
      capacityMin: "Total capacity must be at least 1.",
      adultsMin: "There must be at least 1 adult allowed.",
      adultsMax: "The adult limit cannot be greater than the total capacity.",
      childrenRange: "The children limit must be between 0 and the total capacity.",
      infantsRange: "The infant limit must be between 0 and the total capacity.",
      roomsMin: "The number of rooms must be at least 1.",
      notAuth: "User not authenticated.",
    },
    toasts: {
      updated: "Type updated.",
      created: "Type created.",
      roomsSynced: "Physical rooms generated/adjusted successfully.",
    },
  },

  rate: {
    registerHeader: "Register room type",
    editHeader: "Update rate - {name}",
    createHeader: "Register rate - {name}",
    processing: "Processing, please wait.",
    general: "General",
    namePlaceholder: "Custom name",
    validityLabel: "Validity period",
    permanent: "Permanent",
    custom: "Custom",
    startDatePlaceholder: "Start date",
    endDatePlaceholder: "End date",
    validityNoteCustom: "Rate validity period (optional)",
    validityNotePermanent: "Rate valid permanently",
    pricePerNight: "Price per night",
    taxIncluded: "Taxes included",
    occupancy: "Occupancy",
    guestsNumber: "Number of guests",
    minStay: "Minimum Stay",
    minStayNote: "Minimum nights to bill",
    maxStay: "Maximum Stay",
    maxStayNote: "Limit of nights to book",
    errors: {
      rangeRequired: "You must select a valid date range",
      startAfterEnd: "The start date cannot be later than the end date",
      minStay:
        "The minimum stay is {minStay} nights, but the selected range has {nights} nights",
    },
  },
};
